import { isHandoffUrl, readHandoffResult } from '@/auth/handoff-url';
import {
  POPUP_FLAG_KEY,
  POPUP_MESSAGE_SOURCE,
  type PopupMessage,
} from '@/auth/popup-login';
import { storeToken } from '@/auth/token-storage';
import { paths } from '@/routes/paths';

/** `'handled-in-popup'`: this document is closing, so nothing should render into it. */
export type HandoffOutcome = 'handled-in-popup' | 'continue';

function hasOpener(): boolean {
  return Boolean(window.opener) && window.opener !== window;
}

function takePopupFlag(): boolean {
  try {
    const isPopup = sessionStorage.getItem(POPUP_FLAG_KEY) === '1';
    sessionStorage.removeItem(POPUP_FLAG_KEY);
    return isPopup;
  } catch {
    return false;
  }
}

function reportToOpener(url: URL): void {
  const message: PopupMessage = {
    source: POPUP_MESSAGE_SOURCE,
    ...readHandoffResult(url),
  };
  try {
    window.opener.postMessage(message, window.location.origin);
  } catch {
    // Opener gone; its own polling covers it.
  }
}

export function consumeLoginRedirect(): HandoffOutcome {
  const url = new URL(window.location.href);

  if (isHandoffUrl(url) && hasOpener()) {
    reportToOpener(url);
    if (takePopupFlag()) {
      window.close();
      return 'handled-in-popup';
    }
  }

  // Consume the token before React renders so the router never sees it.
  if (url.pathname !== paths.authLogin) {
    return 'continue';
  }

  const token = url.searchParams.get('token');
  if (token) {
    storeToken(token);
  }
  window.history.replaceState(
    null,
    '',
    token ? paths.app : `${paths.login}?error=auth_failed`
  );
  return 'continue';
}
